/**
 * Comprehensive configuration for all 10 Tamba financial services.
 * Each service defines assessment questions, calculator fields, and metadata.
 */

const SERVICES_CONFIG = {
  "debt-management": {
    id: "debt-management",
    title: "Debt Management",
    tagline: "Take control of your debt, not the other way around",
    description: "Our AI analyzes your debt portfolio and creates a personalized payoff strategy.",
    color: "bg-red-500",
    accent: "text-red-500",
    accentBg: "bg-red-50 dark:bg-red-950/30",
    ring: "ring-red-500/30",
    assessmentQuestions: [
      { key: "monthly_income", label: "Monthly Income ($)", type: "number", placeholder: "5000" },
      { key: "minimum_only", label: "Are you only making minimum payments?", type: "toggle" },
      {
        key: "debts",
        label: "Your Debts",
        type: "debt_list",
        fields: [
          { key: "name", label: "Debt Name", type: "text", placeholder: "Credit Card" },
          { key: "type", label: "Type", type: "select", options: ["credit_card", "student_loan", "auto_loan", "mortgage", "personal_loan", "medical", "other"] },
          { key: "balance", label: "Balance ($)", type: "number", placeholder: "5000" },
          { key: "rate", label: "Interest Rate (%)", type: "number", placeholder: "18.9" },
          { key: "min_payment", label: "Min Payment ($)", type: "number", placeholder: "150" },
        ],
      },
    ],
    calculatorTitle: "Debt Payoff Calculator",
    calculatorDescription: "Compare snowball vs avalanche payoff strategies",
    metricLabels: { total_debt: "Total Debt", monthly_payments: "Monthly Payments", highest_rate: "Highest Rate", debt_to_income: "Debt-to-Income %", num_debts: "Number of Debts" },
    metricFormats: { total_debt: "currency", monthly_payments: "currency", highest_rate: "percent", debt_to_income: "percent", num_debts: "number" },
  },

  "insurance-risk": {
    id: "insurance-risk",
    title: "Insurance & Risk Management",
    tagline: "Protect what matters most",
    description: "Evaluate your current coverage, identify gaps, and optimize your insurance portfolio.",
    color: "bg-blue-600",
    accent: "text-blue-600",
    accentBg: "bg-blue-50 dark:bg-blue-950/30",
    ring: "ring-blue-600/30",
    assessmentQuestions: [
      { key: "annual_income", label: "Annual Income ($)", type: "number", placeholder: "75000" },
      { key: "dependents", label: "Number of Dependents", type: "number", placeholder: "0" },
      { key: "has_life_insurance", label: "Do you have life insurance?", type: "toggle" },
      { key: "life_insurance_amount", label: "Life Insurance Coverage ($)", type: "number", placeholder: "0", showIf: "has_life_insurance" },
      { key: "has_health_insurance", label: "Do you have health insurance?", type: "toggle" },
      { key: "has_home_insurance", label: "Do you have home/renters insurance?", type: "toggle" },
      { key: "has_disability_insurance", label: "Do you have disability insurance?", type: "toggle" },
    ],
    calculatorTitle: "Coverage Gap Analyzer",
    calculatorDescription: "See where you're protected and where you're exposed",
    metricLabels: { coverage_types: "Coverage Types", recommended_life: "Recommended Life Coverage", current_life: "Current Life Coverage", dependents: "Dependents" },
    metricFormats: { coverage_types: "fraction:total_types", recommended_life: "currency", current_life: "currency", dependents: "number" },
  },

  "investments": {
    id: "investments",
    title: "Investments",
    tagline: "Grow your wealth with confidence",
    description: "AI-powered portfolio management tailored to your risk tolerance and goals.",
    color: "bg-teal-500",
    accent: "text-teal-500",
    accentBg: "bg-teal-50 dark:bg-teal-950/30",
    ring: "ring-teal-500/30",
    assessmentQuestions: [
      { key: "portfolio_value", label: "Current Portfolio Value ($)", type: "number", placeholder: "50000" },
      { key: "age", label: "Your Age", type: "number", placeholder: "30" },
      { key: "risk_tolerance", label: "Risk Tolerance (1-10)", type: "slider", min: 1, max: 10, step: 1 },
      { key: "time_horizon", label: "Investment Time Horizon (years)", type: "number", placeholder: "20" },
      { key: "monthly_investment", label: "Monthly Investment Amount ($)", type: "number", placeholder: "500" },
    ],
    calculatorTitle: "Portfolio Growth Projector",
    calculatorDescription: "See how your investments could grow over time",
    metricLabels: { portfolio_value: "Current Portfolio", equity_pct: "Equity Allocation", bond_pct: "Bond Allocation", projected_value: "Projected Value", monthly_investment: "Monthly Investment" },
    metricFormats: { portfolio_value: "currency", equity_pct: "percent", bond_pct: "percent", projected_value: "currency", monthly_investment: "currency" },
  },

  "pension-funds": {
    id: "pension-funds",
    title: "Voluntary Pension Funds",
    tagline: "Secure your future, starting today",
    description: "Maximize your pension contributions with smart allocation strategies.",
    color: "bg-pink-500",
    accent: "text-pink-500",
    accentBg: "bg-pink-50 dark:bg-pink-950/30",
    ring: "ring-pink-500/30",
    assessmentQuestions: [
      { key: "monthly_income", label: "Monthly Income ($)", type: "number", placeholder: "6000" },
      { key: "contribution_pct", label: "Your Contribution (%)", type: "number", placeholder: "6" },
      { key: "employer_match_pct", label: "Employer Match (%)", type: "number", placeholder: "5" },
      { key: "current_balance", label: "Current Pension Balance ($)", type: "number", placeholder: "25000" },
      { key: "retirement_age", label: "Target Retirement Age", type: "number", placeholder: "65" },
      { key: "current_age", label: "Current Age", type: "number", placeholder: "30" },
    ],
    calculatorTitle: "Contribution Optimizer",
    calculatorDescription: "Find the optimal contribution level for your retirement goals",
    metricLabels: { contribution_pct: "Your Contribution", employer_match: "Employer Match", monthly_contrib: "Monthly Contribution", projected_balance: "Projected Balance", leaving_on_table: "Money Left on Table/yr" },
    metricFormats: { contribution_pct: "percent", employer_match: "percent", monthly_contrib: "currency", projected_balance: "currency", leaving_on_table: "currency" },
  },

  "retirement-income": {
    id: "retirement-income",
    title: "Retirement Incomes",
    tagline: "Enjoy the retirement you deserve",
    description: "Plan retirement income streams with precision and sustainable withdrawal strategies.",
    color: "bg-amber-500",
    accent: "text-amber-500",
    accentBg: "bg-amber-50 dark:bg-amber-950/30",
    ring: "ring-amber-500/30",
    assessmentQuestions: [
      { key: "current_age", label: "Current Age", type: "number", placeholder: "35" },
      { key: "retirement_age", label: "Planned Retirement Age", type: "number", placeholder: "65" },
      { key: "total_savings", label: "Total Retirement Savings ($)", type: "number", placeholder: "100000" },
      { key: "monthly_expenses", label: "Expected Monthly Expenses in Retirement ($)", type: "number", placeholder: "4000" },
      { key: "social_security_monthly", label: "Expected Social Security/Pension ($/month)", type: "number", placeholder: "1500" },
      { key: "other_income_monthly", label: "Other Expected Income ($/month)", type: "number", placeholder: "0" },
    ],
    calculatorTitle: "Retirement Income Projector",
    calculatorDescription: "Will your money last through retirement?",
    metricLabels: { projected_savings: "Projected Savings at Retirement", monthly_withdrawal: "Monthly Withdrawal (4%)", total_monthly_income: "Total Monthly Income", income_gap: "Monthly Income Gap", years_of_money: "Years Funds Will Last" },
    metricFormats: { projected_savings: "currency", monthly_withdrawal: "currency", total_monthly_income: "currency", income_gap: "currency", years_of_money: "years" },
  },

  "aged-care": {
    id: "aged-care",
    title: "Aged Care",
    tagline: "Compassionate planning for the years ahead",
    description: "Navigate aged care costs, eligibility, and funding options with clarity.",
    color: "bg-purple-500",
    accent: "text-purple-500",
    accentBg: "bg-purple-50 dark:bg-purple-950/30",
    ring: "ring-purple-500/30",
    assessmentQuestions: [
      { key: "current_age", label: "Current Age", type: "number", placeholder: "70" },
      { key: "health_status", label: "Health Status (1=Poor, 10=Excellent)", type: "slider", min: 1, max: 10, step: 1 },
      { key: "care_preference", label: "Preferred Care Type", type: "select", options: [{ value: "home", label: "Home Care" }, { value: "facility", label: "Residential Facility" }, { value: "both", label: "Open to Both" }] },
      { key: "total_assets", label: "Total Assets ($)", type: "number", placeholder: "500000" },
      { key: "annual_income", label: "Annual Income ($)", type: "number", placeholder: "40000" },
    ],
    calculatorTitle: "Care Cost Estimator",
    calculatorDescription: "Estimate your aged care costs and funding needs",
    metricLabels: { home_care_annual: "Home Care Cost/yr", facility_annual: "Facility Cost/yr", years_covered: "Years Covered", govt_eligible: "Govt. Support Eligible" },
    metricFormats: { home_care_annual: "currency", facility_annual: "currency", years_covered: "years", govt_eligible: "boolean" },
  },

  "estate-planning": {
    id: "estate-planning",
    title: "Estate Planning",
    tagline: "Leave a legacy, not a burden",
    description: "Protect your family's future with wills, trusts, and succession strategies.",
    color: "bg-indigo-500",
    accent: "text-indigo-500",
    accentBg: "bg-indigo-50 dark:bg-indigo-950/30",
    ring: "ring-indigo-500/30",
    assessmentQuestions: [
      { key: "has_will", label: "Do you have a will?", type: "toggle" },
      { key: "has_trust", label: "Do you have a trust?", type: "toggle" },
      { key: "has_power_of_attorney", label: "Do you have power of attorney designated?", type: "toggle" },
      { key: "has_guardianship_plan", label: "Do you have a guardianship plan?", type: "toggle" },
      { key: "has_life_insurance", label: "Do you have life insurance for estate?", type: "toggle" },
      { key: "num_beneficiaries", label: "Number of Beneficiaries", type: "number", placeholder: "0" },
      { key: "estate_value", label: "Total Estate Value ($)", type: "number", placeholder: "500000" },
    ],
    calculatorTitle: "Estate Readiness Score",
    calculatorDescription: "How prepared is your estate plan?",
    metricLabels: { checklist_complete: "Items Complete", estate_value: "Estate Value", num_beneficiaries: "Beneficiaries" },
    metricFormats: { checklist_complete: "fraction:checklist_total", estate_value: "currency", num_beneficiaries: "number" },
  },

  "cashflow-budgeting": {
    id: "cashflow-budgeting",
    title: "Cashflow Management & Budgeting",
    tagline: "Every dollar has a purpose",
    description: "AI-powered budgeting that adapts to your life and optimizes cash flow.",
    color: "bg-orange-500",
    accent: "text-orange-500",
    accentBg: "bg-orange-50 dark:bg-orange-950/30",
    ring: "ring-orange-500/30",
    assessmentQuestions: [
      { key: "monthly_income", label: "Monthly Income ($)", type: "number", placeholder: "6000" },
      { key: "fixed_expenses", label: "Fixed Expenses (rent, loans, subscriptions) ($)", type: "number", placeholder: "2500" },
      { key: "variable_expenses", label: "Variable Expenses (food, transport, fun) ($)", type: "number", placeholder: "1500" },
      { key: "current_savings_rate", label: "Current Savings Rate (%)", type: "number", placeholder: "10" },
    ],
    calculatorTitle: "Budget Optimizer",
    calculatorDescription: "Compare your budget against the 50/30/20 rule",
    metricLabels: { monthly_income: "Monthly Income", actual_savings: "Monthly Savings", savings_rate: "Savings Rate", needs_pct: "Needs (target 50%)", wants_pct: "Wants (target 30%)" },
    metricFormats: { monthly_income: "currency", actual_savings: "currency", savings_rate: "percent", needs_pct: "percent", wants_pct: "percent" },
  },

  "taxation-planning": {
    id: "taxation-planning",
    title: "Taxation Planning",
    tagline: "Keep more of what you earn",
    description: "Strategic tax planning to minimize your burden legally and ethically.",
    color: "bg-emerald-500",
    accent: "text-emerald-500",
    accentBg: "bg-emerald-50 dark:bg-emerald-950/30",
    ring: "ring-emerald-500/30",
    assessmentQuestions: [
      { key: "annual_income", label: "Annual Income ($)", type: "number", placeholder: "75000" },
      { key: "filing_status", label: "Filing Status", type: "select", options: [{ value: "single", label: "Single" }, { value: "married_filing_jointly", label: "Married Filing Jointly" }, { value: "married_filing_separately", label: "Married Filing Separately" }, { value: "head_of_household", label: "Head of Household" }] },
      { key: "deductions_used", label: "Deductions You Currently Claim", type: "multiselect", options: [
        { value: "mortgage_interest", label: "Mortgage Interest" },
        { value: "state_local_tax", label: "State & Local Tax" },
        { value: "charitable", label: "Charitable Contributions" },
        { value: "medical", label: "Medical Expenses" },
        { value: "education", label: "Education Credits" },
        { value: "retirement_401k", label: "401k/IRA Contributions" },
        { value: "hsa", label: "HSA Contributions" },
        { value: "home_office", label: "Home Office" },
        { value: "student_loan_interest", label: "Student Loan Interest" },
      ]},
      { key: "investment_income", label: "Investment Income ($)", type: "number", placeholder: "0" },
      { key: "charitable_contributions", label: "Charitable Contributions ($)", type: "number", placeholder: "0" },
      { key: "has_business_income", label: "Do you have business/self-employment income?", type: "toggle" },
    ],
    calculatorTitle: "Tax Savings Estimator",
    calculatorDescription: "Find deductions you might be missing",
    metricLabels: { tax_bracket: "Tax Bracket", deductions_used: "Deductions Claimed", deductions_missed: "Deductions Missed", potential_savings: "Potential Tax Savings" },
    metricFormats: { tax_bracket: "text", deductions_used: "number", deductions_missed: "number", potential_savings: "currency" },
  },

  "salary-packaging": {
    id: "salary-packaging",
    title: "Salary Packaging",
    tagline: "Maximize your take-home pay",
    description: "Structure your remuneration to take advantage of tax benefits.",
    color: "bg-cyan-600",
    accent: "text-cyan-600",
    accentBg: "bg-cyan-50 dark:bg-cyan-950/30",
    ring: "ring-cyan-600/30",
    assessmentQuestions: [
      { key: "gross_salary", label: "Gross Annual Salary ($)", type: "number", placeholder: "80000" },
      { key: "employment_type", label: "Employment Type", type: "select", options: [{ value: "private", label: "Private Sector" }, { value: "public", label: "Public Sector" }, { value: "nfp", label: "Not-for-Profit" }] },
      { key: "current_items", label: "Items Currently Packaged", type: "multiselect", options: [
        { value: "car_novated_lease", label: "Car (Novated Lease)" },
        { value: "laptop", label: "Laptop/Computer" },
        { value: "phone", label: "Mobile Phone" },
        { value: "professional_memberships", label: "Professional Memberships" },
        { value: "self_education", label: "Self Education" },
        { value: "airport_lounge", label: "Airport Lounge" },
        { value: "income_protection", label: "Income Protection Insurance" },
        { value: "additional_super", label: "Additional Super Contributions" },
      ]},
      { key: "has_hecs_debt", label: "Do you have HECS/HELP debt?", type: "toggle" },
    ],
    calculatorTitle: "Take-Home Pay Optimizer",
    calculatorDescription: "See how much more you could take home with packaging",
    metricLabels: { gross_salary: "Gross Salary", current_savings: "Current Tax Savings", potential_additional_savings: "Additional Savings Available", tax_benefit: "Total Tax Benefit", items_used: "Items Packaged" },
    metricFormats: { gross_salary: "currency", current_savings: "currency", potential_additional_savings: "currency", tax_benefit: "currency", items_used: "fraction:items_available" },
  },
};

export default SERVICES_CONFIG;
